/**
 * Recompute the backend-emitted `actual_trajectory` of a spreadsheet
 * record with user-excluded sample anchors removed.
 *
 * Established 2026-09-23 by PFD-BUG-007; indexing corrected
 * 2026-09-23 by PFD-BUG-008 after the DR252.1 2026-10-07 500 g
 * off-by-one report. See RULE-IMP-SMP-001 in `PFD_SPEC.md`.
 *
 * ──────────────────────────────────────────────────────────────────
 * Index contract (CRITICAL — do not change without a parallel edit
 * to `utils/generateIntelligence.js::buildComparisonsFromRecord`):
 * ──────────────────────────────────────────────────────────────────
 *   • `comparisons[i]` (and therefore the Per-Sample Deviation table
 *     row with `sample_number = i + 1`) comes from
 *     `activeRecord.sample_points[i]`. Every genuine sample
 *     indexes from 0 — there is NO separate "arrival" index -1 in
 *     Spreadsheet mode (unlike GrowthModelAnalysis, which keeps
 *     arrival outside the comparisons array).
 *   • Each is_sample trajectory point therefore maps 1:1 to its
 *     comparison index, in chronological order.
 *   • When `options.samplePoints` is supplied (the preferred path),
 *     we pair trajectory anchors to comparison indices by DATE —
 *     stable against any is_sample flag drift and the recommended
 *     identity per the PFD-BUG-008 fix.
 *
 * ──────────────────────────────────────────────────────────────────
 * Arrival baseline
 * ──────────────────────────────────────────────────────────────────
 *   The arrival weight is the record's stocking weight — stored on
 *   `activeRecord.input_average_weight` and valid on day 0 regardless
 *   of whether sample_points[0] is at day 0. The caller passes it as
 *   `options.inputAverageWeight`. When the first kept anchor is not
 *   at day 0 (either because sample_points[0] is later, or because
 *   compIdx 0 was excluded), days from 0 up to the first kept anchor
 *   grow from this baseline using the model-weight growth ratio.
 *
 * ──────────────────────────────────────────────────────────────────
 * Contract
 * ──────────────────────────────────────────────────────────────────
 *   • Historical daily feed, mortality, temperature and dates are
 *     NEVER touched — the helper only re-emits weights on a cloned
 *     actual-trajectory array.
 *   • Empty excluded set → returns the ORIGINAL reference unchanged
 *     (zero drift on the common path).
 */
export function recomputeActualTrajectoryWithExclusions(
  actualTrajectory,
  modelTrajectory,
  excludedIndices,
  options = {},
) {
  if (!Array.isArray(actualTrajectory) || actualTrajectory.length === 0) {
    return actualTrajectory;
  }
  const excluded = excludedIndices instanceof Set
    ? excludedIndices
    : new Set(Array.isArray(excludedIndices) ? excludedIndices : []);
  if (excluded.size === 0) return actualTrajectory;

  const samplePoints = Array.isArray(options.samplePoints) ? options.samplePoints : null;

  // ── Build the anchor list ─────────────────────────────────────────
  // Preferred: date-based identity from `samplePoints` (stable against
  // is_sample flag drift). Fallback: positional ordinal of is_sample
  // trajectory points. BOTH paths assign comparison index starting at
  // 0 for the first sample event (NO arrival at -1).
  const anchors = [];
  if (samplePoints && samplePoints.length > 0) {
    const indexByDate = new Map();
    for (let i = 0; i < actualTrajectory.length; i++) {
      const d = actualTrajectory[i]?.date;
      if (d != null && !indexByDate.has(d)) indexByDate.set(d, i);
    }
    // Sort sample_points chronologically so comparison indices
    // match the Per-Sample Deviation table enumeration (which is
    // chronological via buildComparisonsFromRecord sorting
    // downstream in calculateMissingFishIntelligence).
    const sortedSamples = [...samplePoints]
      .filter((sp) => sp && sp.date != null)
      .sort((a, b) => String(a.date).localeCompare(String(b.date)));
    sortedSamples.forEach((sp, compIdx) => {
      const i = indexByDate.get(sp.date);
      const w = typeof sp.weight === 'number' ? sp.weight : parseFloat(sp.weight);
      if (i == null || !Number.isFinite(w) || w <= 0) return;
      anchors.push({ i, weight: w, compIdx });
    });
  } else {
    let compIdx = 0;
    for (let i = 0; i < actualTrajectory.length; i++) {
      const pt = actualTrajectory[i];
      const w = pt?.weight;
      if (pt?.is_sample && typeof w === 'number' && w > 0) {
        anchors.push({ i, weight: w, compIdx });
        compIdx += 1;
      }
    }
  }
  if (anchors.length === 0) return actualTrajectory;

  const kept = anchors.filter((a) => !excluded.has(a.compIdx));
  if (kept.length === anchors.length) {
    // Caller passed excluded indices we don't have anchors for —
    // nothing to recompute. Return the original reference so the
    // common path is zero-copy.
    return actualTrajectory;
  }

  const modelAt = (idx) => {
    if (!Array.isArray(modelTrajectory)) return null;
    const m = modelTrajectory[idx];
    const v = typeof m?.weight === 'number' ? m.weight : parseFloat(m?.weight);
    return Number.isFinite(v) ? v : null;
  };

  // Arrival baseline — valid at day 0 regardless of sample timing.
  const inputW = typeof options.inputAverageWeight === 'number'
    ? options.inputAverageWeight
    : parseFloat(options.inputAverageWeight);
  const arrivalWeight = Number.isFinite(inputW) && inputW > 0
    ? inputW
    : (typeof actualTrajectory[0]?.weight === 'number' ? actualTrajectory[0].weight : null);

  // Shallow-clone every point so the caller never mutates the record cache.
  const next = actualTrajectory.map((pt) => ({ ...pt }));

  // Strip is_sample from excluded anchors so dependent UI (sample
  // dots, deviation markers, 500 g annotation) stops rendering them.
  anchors.forEach((a) => {
    if (excluded.has(a.compIdx)) {
      next[a.i].is_sample = false;
    }
  });

  // Grow weights across [fromDay+1 .. toDay-1] from `fromWeight` using
  // the model-weight growth ratio seeded at `fromDay`.
  const growSpan = (fromDay, fromWeight, toDay) => {
    if (fromDay < 0) fromDay = 0;
    const anchorModel = modelAt(fromDay);
    if (anchorModel == null || anchorModel <= 0) return;
    for (let d = fromDay + 1; d < toDay; d++) {
      const m = modelAt(d);
      if (m == null) continue;
      next[d].weight = fromWeight * (m / anchorModel);
    }
  };

  if (kept.length === 0) {
    // Everything excluded — grow pure model-ratio from the arrival
    // baseline. Still returns a valid trajectory so biomass + feed
    // calculations downstream don't go blank.
    if (arrivalWeight != null && arrivalWeight > 0) {
      next[0].weight = arrivalWeight;
      growSpan(0, arrivalWeight, next.length);
    }
    return next;
  }

  // Pre-first-kept-anchor span. If the first kept anchor isn't at day 0,
  // seed from arrival baseline so the growth curve has a reference.
  const first = kept[0];
  if (first.i > 0 && arrivalWeight != null && arrivalWeight > 0) {
    next[0].weight = arrivalWeight;
    growSpan(0, arrivalWeight, first.i);
  }
  next[first.i].weight = first.weight;

  // Pairwise kept-anchor spans.
  for (let s = 0; s < kept.length - 1; s++) {
    const a = kept[s];
    const b = kept[s + 1];
    growSpan(a.i, a.weight, b.i);
    next[b.i].weight = b.weight;
  }

  // Post-last-kept-anchor extension.
  const last = kept[kept.length - 1];
  growSpan(last.i, last.weight, next.length);

  return next;
}

export default recomputeActualTrajectoryWithExclusions;
